import Card, { type CardProps } from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Reporting_ChartType, type Reporting_ReportFilter } from '@tyro/api';
import {
  Chart,
  type ChartOptions,
  type ChartProps,
  mapChartQueryForChart,
  useChart,
} from '@tyro/core';
import { useRunReportCharts } from '@tyro/reporting';
import { useDeferredValue, useMemo } from 'react';
import { useMeasure } from 'react-use';

export interface ReportChartWidgetProps extends CardProps {
  title?: string;
  chartType: Reporting_ChartType;
  filter: Reporting_ReportFilter;
  options: ChartOptions;
}

const chartTypeMap = new Map<Reporting_ChartType, ChartProps['type']>([
  [Reporting_ChartType.Bar, 'bar'],
  [Reporting_ChartType.Donut, 'donut'],
]);

export function ReportChartWidget({
  title,
  chartType,
  filter,
  options,
  ...cardProps
}: ReportChartWidgetProps) {
  const { data: charts, isLoading } = useRunReportCharts(filter);
  const [stackRef, { height: stackHeight, width: stackWidth }] =
    useMeasure<HTMLDivElement>();
  const [titleRef, { height: titleHeight }] = useMeasure<HTMLHeadingElement>();

  const { series, options: mappedOptions } = useMemo(() => {
    const data = charts?.get(chartType);
    return mapChartQueryForChart(data, options);
  }, [charts, chartType]);

  const chartOptions = useChart(mappedOptions);
  const deferredStackHeight = useDeferredValue(stackHeight);
  const deferredStackWidth = useDeferredValue(stackWidth);

  return (
    <Card
      {...cardProps}
      sx={{
        flex: 1,
        overflow: 'visible',
        display: 'flex',
        width: '100%',
        ...cardProps.sx,
      }}
    >
      <Stack ref={stackRef} p={2} flex={1} width="100%">
        {title && (
          <Typography ref={titleRef} variant="h6" component="h2">
            {title}
          </Typography>
        )}
        {deferredStackHeight > 0 && deferredStackWidth > 0 && (
          <Chart
            type={chartTypeMap.get(chartType)}
            series={series}
            options={chartOptions}
            height={deferredStackHeight - (titleHeight ?? 0)}
            width={deferredStackWidth}
            isLoading={isLoading}
          />
        )}
      </Stack>
    </Card>
  );
}
